
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import {
  ChatInterface,
  CommentCreateInterface,
  MessageInterface, ProfileMapInterface,
} from '@/services/QC-V2/chat/interfaces';
import ChatMessageImageDialog from '@/components/dialogs/qa/chat/ChatMessageImageDialog.vue';
import { CommentRate, CommentRepositoryInterface } from '@/services/QC-V2/comment/interfaces';
import { Roles } from '@/services/Roles';
import DeleteCommentDialog from '@/components/dialogs/qc-v2/comment/DeleteCommentDialog.vue';

@Component({
  mixins: [
    Themeable,
  ],
  components: {
    DeleteCommentDialog,
    ChatMessageImageDialog,
  },
  inject: [
    'commentRepositoryV2',
  ],
})
export default class ChatMessages extends Vue {
  @Prop() private chat!: ChatInterface;
  @Prop() private profilesMap!: ProfileMapInterface;
  @Prop({ default: false }) private disabledMessageComments: boolean;

  private loading: boolean = true;

  private commentRepositoryV2!: CommentRepositoryInterface;

  private messageAuthorsMap: { [key: string]: string } = {
    'System Message': 'System Message',
  };

  private urlForDialog: string = '';
  private imageDialog: string | false = false;

  private messageIdForCommentBox: number = 0;
  private messageComment: string = '';

  private commentRates: typeof CommentRate = CommentRate;
  private commentRate: CommentRate = CommentRate.NOT_RATED;

  private commentIdForEdit: number = 0;
  private editCommentText: string = '';

  private userRoles: object = Roles;

  private deleteCommentDialogVisible: boolean = false;
  private deleteCommentDialogEvent!: string;
  private deleteCommentDialogData!: { messageId: number, commentId: number };

  private async created(): Promise<void> {
    this.createMessageAuthorsMap();
    this.loading = false;
  }

  private userHasScopes(scope: string): boolean {
    return this.$store.getters.scopes.includes(scope);
  }

  @Watch('chat')
  private async updateExternalData(): Promise<void> {
    this.loading = true;
    this.createMessageAuthorsMap();
    this.loading = false;
  }

  private createMessageAuthorsMap(): void {
    this.chat.agents.forEach((agent) => {
      if (!this.messageAuthorsMap[agent.email]) {
        this.messageAuthorsMap[agent.email] = agent.name;
      }
    });

    this.chat.supervisors.forEach((supervisor) => {
      if (!this.messageAuthorsMap[supervisor.email]) {
        this.messageAuthorsMap[supervisor.email] = supervisor.name;
      }
    });
  }

  private bindMessageClass(message: MessageInterface): string {
    if (message.isWhisper) {
      return 'whisper-message';
    }

    if (message.authorId === 'System Message') {
      return 'system-message';
    }

    return message.authorId.includes('@') ? 'agent-message' : 'visitor-message';
  }

  private showImage(url: string): void {
    this.imageDialog = 'chat-message-image-dialog';
    this.urlForDialog = url;
  }

  private dividerNeeded(messageTimestamp: Date, nextMessageIndex: number): boolean {
    if (nextMessageIndex === 1 || !this.chat.messages[nextMessageIndex]) {
      return true;
    }

    return String(messageTimestamp).substr(0, 16)
      !== String(this.chat.messages[nextMessageIndex].timestamp).substr(0, 16);
  }

  private showCommentBox(messageId: number): void {
    this.messageComment = '';
    if (messageId === this.messageIdForCommentBox) {
      this.messageIdForCommentBox = 0;
      return;
    }
    this.messageIdForCommentBox = messageId;
  }

  private async createMessageComment(messageId: number, messageIndex: number): Promise<void> {
    this.messageComment = this.messageComment.trim();
    if (!this.messageComment) {
      return;
    }

    const commentData: CommentCreateInterface = {
      author: this.$store.getters.id,
      text: this.messageComment,
      rate: this.commentRate,
      timestamp: new Date().toISOString(),
    };

    try {
      const comment = await this.commentRepositoryV2.createMessageComment(messageId, commentData);
      this.$successDialog.show('Comment created successfully!');
      this.chat.messages[messageIndex].comments.push(comment);
      this.messageComment = '';
      this.commentRate = CommentRate.NOT_RATED;
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private showEditBox(commentId: number, commentText: string): void {
    if (this.commentIdForEdit === commentId) {
      this.editCommentText = '';
      this.commentIdForEdit = 0;
      return;
    }

    this.editCommentText = commentText;
    this.commentIdForEdit = commentId;
  }

  private async editMessageComment(
    messageId: number,
    messageIndex: number,
    commentId: number,
    commentIndex: number,
  ): Promise<void> {
    try {
      const commentData: CommentCreateInterface = {
        author: this.chat.messages[messageIndex].comments[commentIndex].author,
        text: this.editCommentText,
        rate: this.commentRate,
        timestamp: new Date().toISOString(),
      };

      await this.commentRepositoryV2.updateMessageComment(messageId, commentId, commentData);
      this.$successDialog.show('Comment Edited Successfully!');
      this.chat.messages[messageIndex].comments[commentIndex].text = this.editCommentText;

      this.commentIdForEdit = 0;
      this.editCommentText = '';
      this.commentRate = CommentRate.NOT_RATED;
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private showDeleteMessageCommentDialog(messageId: number, commentId: number): void {
    this.deleteCommentDialogData = { commentId, messageId };
    this.deleteCommentDialogEvent = 'message-comment-deleted';
    this.deleteCommentDialogVisible = true;
  }

  private async deleteComment(data: { messageId: number, commentId: number }): Promise<void> {
    try {
      await this.commentRepositoryV2.deleteMessageComment(data.messageId, data.commentId);
      const messageIndex = this.chat.messages.findIndex((message) => message.id === data.messageId);
      this.$successDialog.show('Comment Deleted Successfully!');
      this.chat.messages[messageIndex].comments = this.chat.messages[messageIndex].comments
        .filter((comment) => comment.id !== data.commentId);
      this.deleteCommentDialogVisible = false;
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private shouldShowAvatar(message: MessageInterface): boolean {
    return message.authorId !== 'System Message';
  }

  private getAvatar(message: MessageInterface): string {
    const agent = this.chat.agents.find((a) => a.email === message.authorId);
    if (agent && agent.avatar) {
      return agent.avatar;
    }

    if (agent && !agent.avatar) {
      return `https://ui-avatars.com/api/?name=${agent.name}&background=random`;
    }

    const nameArr = this.chat.visitor.formName.split(' ');
    return `https://ui-avatars.com/api/?name=${nameArr.join('+')}&background=random&&length=${nameArr.length}`;
  }

  private setCommentRate(rate: CommentRate): void {
    if (rate === this.commentRate) {
      this.commentRate = CommentRate.NOT_RATED;
      return;
    }

    this.commentRate = rate;
  }
}
